// Rental appraisal slideover. Opened from the property slideover's Valuation
// tab via the window event "open-appraisal-slideover" { valuationId, address }.
//
// Two steps over one resource (/valuations/:id/appraisal):
//   1. the gap questions, pre-filled from the records, which the agent
//      confirms or corrects, then "Write the appraisal" (POST generate)
//   2. the draft: figures and every section of the prose, editable in
//      place, then Preview (HTML) or Generate PDF (POST render_pdf)
//
// Arrays of paragraphs are edited as one textarea with a blank line between
// paragraphs; lists of {title, body} as rows.

const PARAGRAPH_SECTIONS = ["opinion", "aside"];
const ROW_SECTIONS = ["before_marketing", "next_steps"];

export function appraisalSlideover() {
  return {
    open: false,
    zIndex: 100020,
    valuationId: null,
    address: "",
    loading: false,
    error: null,
    step: "questions",
    state: null,
    answers: {},
    draft: { narrative: {}, figures: {} },
    paragraphs: { opinion: "", aside: "" },
    saving: false,
    generating: false,
    rendering: false,

    init() {
      window.addEventListener("open-appraisal-slideover", (e) => this.openFor(e?.detail || {}));
    },

    async openFor(detail) {
      const store = Alpine.store?.("slideovers");
      if (store?.getNextZIndex) this.zIndex = store.getNextZIndex();
      this.valuationId = detail.valuationId;
      this.address = detail.address || "";
      this.error = null;
      this.open = true;
      this.toggleAiFab(false);
      await this.load();
    },

    close() {
      this.open = false;
      this.toggleAiFab(true);
    },

    // The AI FAB sits over the slideover's buttons, so hide it while the
    // appraisal is open, as the email slideover does.
    toggleAiFab(visible) {
      const fab = document.querySelector(".ai-fab-container");
      if (!fab) return;
      fab.style.transition = "opacity 0.2s ease";
      if (visible) {
        fab.style.display = "";
        requestAnimationFrame(() => { fab.style.opacity = "1"; });
      } else {
        fab.style.opacity = "0";
        setTimeout(() => { fab.style.display = "none"; }, 200);
      }
    },

    get url() {
      return `/valuations/${this.valuationId}/appraisal`;
    },

    headers() {
      return {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      };
    },

    async request(method, path, body) {
      const response = await fetch(path, { method, headers: this.headers(), body: body ? JSON.stringify(body) : undefined });
      let data = null;
      try { data = await response.json(); } catch (_) { data = null; }
      if (!response.ok) {
        throw new Error((data && data.errors && data.errors.join(", ")) || `Server error ${response.status}`);
      }
      return data;
    },

    async load() {
      this.loading = true;
      try {
        this.apply(await this.request("GET", `${this.url}.json`));
        this.step = this.state.narrative ? "draft" : "questions";
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },

    // Server state → the two editable copies (answers, draft).
    apply(state) {
      this.state = state;
      const answers = {};
      (state.questions || []).forEach((q) => { answers[q.key] = q.value ?? ""; });
      this.answers = { ...answers, ...(state.answers || {}) };
      // The services put forward are a list of { key, fee }; the question's
      // value already carries the fallback when nothing has been saved.
      if (!Array.isArray(this.answers.services) || this.answers.services.length === 0) {
        const q = (state.questions || []).find((x) => x.key === "services");
        this.answers.services = Array.isArray(q?.value) ? q.value.map((s) => ({ key: s.key, fee: s.fee ?? "" })) : [];
      }

      const narrative = JSON.parse(JSON.stringify(state.narrative || {}));
      const figures = state.figures || {};
      this.draft = {
        narrative,
        figures: {
          asking: figures.asking_pence ? figures.asking_pence / 100 : "",
          range_low: figures.range_low_pence ? figures.range_low_pence / 100 : "",
          range_high: figures.range_high_pence ? figures.range_high_pence / 100 : "",
          time_to_let: figures.time_to_let || "",
        },
      };
      PARAGRAPH_SECTIONS.forEach((k) => { this.paragraphs[k] = (narrative[k] || []).join("\n\n"); });
      ROW_SECTIONS.forEach((k) => { if (!Array.isArray(narrative[k])) narrative[k] = []; });
      if (!Array.isArray(narrative.spec)) narrative.spec = [];
      if (!Array.isArray(narrative.highlights)) narrative.highlights = [];
    },

    get answersComplete() {
      return !!(this.answers.furnished && this.answers.occupied && (this.answers.services || []).length > 0);
    },

    // ── Services put forward ──────────────────────────────────────────
    // Ticked in the order the agent chooses; the first leads the letter.
    get leadServiceKey() { return (this.answers.services || [])[0]?.key || null; },
    serviceProposed(key) { return (this.answers.services || []).some((s) => s.key === key); },
    serviceFee(key) { return (this.answers.services || []).find((s) => s.key === key)?.fee ?? ""; },
    toggleService(opt) {
      const list = this.answers.services || [];
      this.answers.services = this.serviceProposed(opt.key)
        ? list.filter((s) => s.key !== opt.key)
        : [...list, { key: opt.key, fee: opt.default_fee || "" }];
    },
    setServiceFee(key, fee) {
      this.answers.services = (this.answers.services || []).map((s) => (s.key === key ? { ...s, fee } : s));
    },

    optionValue(opt) { return Array.isArray(opt) ? opt[0] : opt; },
    optionLabel(opt) { return Array.isArray(opt) ? opt[1] : opt; },

    async saveAnswers() {
      if (this.saving) return;
      this.saving = true; this.error = null;
      try {
        this.apply(await this.request("PATCH", `${this.url}.json`, { answers: this.answers }));
      } catch (e) {
        this.error = e.message;
      } finally {
        this.saving = false;
      }
    },

    async generate() {
      if (this.generating || !this.answersComplete) return;
      this.generating = true; this.error = null;
      try {
        this.apply(await this.request("POST", `${this.url}/generate.json`, { answers: this.answers }));
        this.step = "draft";
      } catch (e) {
        this.error = e.message;
      } finally {
        this.generating = false;
      }
    },

    // The editable draft → the stored shape.
    draftPayload() {
      const narrative = JSON.parse(JSON.stringify(this.draft.narrative));
      PARAGRAPH_SECTIONS.forEach((k) => {
        narrative[k] = this.paragraphs[k].split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
      });
      ROW_SECTIONS.concat(["spec", "highlights"]).forEach((k) => {
        narrative[k] = (narrative[k] || []).filter((row) => Object.values(row).some((v) => v && String(v).trim()));
      });
      const pounds = (v) => (v === "" || v === null || isNaN(Number(v)) ? null : Math.round(Number(v) * 100));
      return {
        narrative,
        figures: {
          asking_pence: pounds(this.draft.figures.asking),
          range_low_pence: pounds(this.draft.figures.range_low),
          range_high_pence: pounds(this.draft.figures.range_high),
          time_to_let: this.draft.figures.time_to_let,
        },
      };
    },

    async saveDraft() {
      if (this.saving) return false;
      this.saving = true; this.error = null;
      try {
        this.apply(await this.request("PATCH", `${this.url}.json`, this.draftPayload()));
        return true;
      } catch (e) {
        this.error = e.message;
        return false;
      } finally {
        this.saving = false;
      }
    },

    async preview() {
      if (await this.saveDraft()) window.open(this.state.preview_url, "_blank");
    },

    async renderPdf() {
      if (this.rendering) return;
      if (!(await this.saveDraft())) return;
      this.rendering = true; this.error = null;
      try {
        this.apply(await this.request("POST", `${this.url}/render_pdf.json`));
        if (this.state.pdf_url) window.open(this.state.pdf_url, "_blank");
      } catch (e) {
        this.error = e.message;
      } finally {
        this.rendering = false;
      }
    },

    addRow(key, blank) {
      this.draft.narrative[key] = (this.draft.narrative[key] || []).concat([blank]);
    },

    removeRow(key, index) {
      this.draft.narrative[key].splice(index, 1);
    },

    money(pence) {
      if (pence === null || pence === undefined) return "—";
      return "£" + Math.round(pence / 100).toLocaleString("en-GB");
    },

    when(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    },
  };
}
